import React from "react";
import {
  Box,
  Button,
  Chip,
  Paper,
  Skeleton,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import GroupsOutlinedIcon from "@mui/icons-material/GroupsOutlined";
import StorefrontOutlinedIcon from "@mui/icons-material/StorefrontOutlined";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import CampaignOutlinedIcon from "@mui/icons-material/CampaignOutlined";
import ContactPhoneOutlinedIcon from "@mui/icons-material/ContactPhoneOutlined";
import RestaurantMenuIcon from "@mui/icons-material/RestaurantMenu";
import EventOutlinedIcon from "@mui/icons-material/EventOutlined";
import SmartToyOutlinedIcon from "@mui/icons-material/SmartToyOutlined";
import SupportAgentOutlinedIcon from "@mui/icons-material/SupportAgentOutlined";
import ShareIcon from "@mui/icons-material/Share";
import RocketLaunchIcon from "@mui/icons-material/RocketLaunch";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import WorkspacePremiumIcon from "@mui/icons-material/WorkspacePremium";
import { SvgIconComponent } from "@mui/icons-material";
import { ISubscriptionPlan } from "../../../redux/subscription/subscription.types";
import { capitalizeWords } from "../../../helpers";
import { PRICING_ACCENT } from "../pricingTheme";

interface IPricingPlanCardProps {
  plan: ISubscriptionPlan;
  /** 0-based index in the grid — drives the "Everything in …" subtitle fallback. */
  index?: number;
  /** Name of the plan that sits one tier below (for the "Everything in X +" line). */
  previousPlanName?: string;
  selectedCurrency?: "INR" | "USD" | "AED" | "EUR";
  cardButtonText?: string;
  isCurrentPlan?: boolean;
  isPaymentPending?: boolean;
  /** Highlight tier (tinted background + solid CTA). */
  highlighted?: boolean;
  handleChoosePlan?: (plan: ISubscriptionPlan) => void;
  onShare?: (plan: ISubscriptionPlan) => void;
  currentBillingMode?: "RECURRING" | "PREPAID";
  onChangeBillingMode?: (plan: ISubscriptionPlan) => void;
}

const CURRENCY_SYMBOL: Record<string, string> = {
  INR: "₹",
  USD: "$",
  EUR: "€",
  AED: "د.إ",
};

/**
 * Keyword → icon rules. The first rule whose keyword appears in the
 * feature name wins, so each row shows an icon that matches its meaning.
 * Falls back to a neutral check mark.
 */
const FEATURE_ICON_RULES: Array<{ match: RegExp; icon: SvgIconComponent }> = [
  { match: /user|seat|member|login|access/i, icon: PersonOutlineIcon },
  { match: /venue|outlet|branch|location/i, icon: StorefrontOutlinedIcon },
  { match: /lead/i, icon: ContactPhoneOutlinedIcon },
  { match: /upsell|revenue|sales|growth|boost/i, icon: TrendingUpIcon },
  { match: /campaign|broadcast|market|promo/i, icon: CampaignOutlinedIcon },
  { match: /menu|food|dish|catalog/i, icon: RestaurantMenuIcon },
  { match: /event|booking|banquet|calendar/i, icon: EventOutlinedIcon },
  { match: /team|staff|group|collaborat/i, icon: GroupsOutlinedIcon },
  { match: /ai|bot|automation|assistant/i, icon: SmartToyOutlinedIcon },
  { match: /support|service|help|agent/i, icon: SupportAgentOutlinedIcon },
];

const getFeatureIcon = (featureName: string): SvgIconComponent => {
  const rule = FEATURE_ICON_RULES.find((r) => r.match.test(featureName));
  return rule ? rule.icon : CheckCircleOutlineIcon;
};

/** Tier icon shown in the header tile, by grid position. */
const TIER_ICONS: SvgIconComponent[] = [
  RocketLaunchIcon,
  AutoAwesomeIcon,
  WorkspacePremiumIcon,
];

const formatAmount = (amount: number | null | undefined, symbol: string) => {
  if (amount === null || amount === undefined) return null;
  return `${symbol}${Number(amount).toLocaleString("en-IN")}`;
};

/** Strips HTML/entities from a description and clamps to a single tagline line. */
const toTagline = (raw?: string): string => {
  if (!raw) return "";
  return raw
    .replace(/<[^>]+>/g, " ")
    .replace(/&nbsp;/gi, " ")
    .replace(/&amp;/gi, "&")
    .replace(/\s+/g, " ")
    .trim();
};

/**
 * Splits a feature label so the leading quantitative token is bold.
 * Bolds the first token when it is a number, or one of a small set of
 * emphasis words (Unlimited / Custom / Single / Create N).
 * Returns [boldPart, restPart].
 */
const splitEmphasis = (label: string): [string, string] => {
  const words = label.trim().split(/\s+/);
  if (words.length === 0) return ["", label];

  const first = words[0];
  const emphasisWords = ["unlimited", "custom", "single", "free"];
  const isNumber = /^\d[\d,]*$/.test(first);
  const isEmphasis = emphasisWords.includes(first.toLowerCase());

  // "Create 50 leads…" → bold "Create 50"
  if (/^create$/i.test(first) && /^\d/.test(words[1] ?? "")) {
    return [`${words[0]} ${words[1]}`, words.slice(2).join(" ")];
  }
  if (isNumber || isEmphasis) {
    return [first, words.slice(1).join(" ")];
  }
  return ["", label];
};

export const PricingPlanCard: React.FC<IPricingPlanCardProps> = ({
  plan,
  index = 0,
  previousPlanName,
  selectedCurrency = "INR",
  cardButtonText,
  isCurrentPlan = false,
  isPaymentPending = false,
  highlighted = false,
  handleChoosePlan,
  onShare,
  currentBillingMode,
  onChangeBillingMode,
}) => {
  const symbol = CURRENCY_SYMBOL[selectedCurrency] ?? "₹";

  const priceEntry = Array.isArray(plan.price)
    ? plan.price.find((p) => p.currency === selectedCurrency) ?? plan.price[0]
    : undefined;

  const baseAmount = priceEntry ? Number(priceEntry.base_amount ?? 0) : 0;
  const strikeAmount = priceEntry ? Number(priceEntry.strikeout_amount ?? 0) : 0;
  const isFree = Boolean(plan.is_free_plan) || baseAmount === 0;

  const isYearly = plan.billing_cycle === "years";
  const cycleLabel = isYearly ? "year" : "month";

  // Yearly pricing uses the "pay for 10 months, get 12" model: the yearly base
  // equals 10x the monthly rate, while a full year at the monthly rate costs 12x.
  // Amount saved = 12x - 10x = 2x the monthly rate = baseAmount / 5.
  const YEARLY_MONTHS_CHARGED = 10;
  const MONTHS_IN_YEAR = 12;
  const savingsAmount =
    isYearly && baseAmount > 0
      ? Math.round(
          (baseAmount / YEARLY_MONTHS_CHARGED) * (MONTHS_IN_YEAR - YEARLY_MONTHS_CHARGED),
        )
      : 0;

  const tagline =
    toTagline(plan.description) ||
    (previousPlanName
      ? `Everything in ${capitalizeWords(previousPlanName)} +`
      : "Ideal for growing teams");

  const features = Array.isArray(plan.subscription_features)
    ? [...plan.subscription_features]
        .filter((f) => f.feature_name && f.show_on_card)
        .sort((a, b) => (a.priority || 0) - (b.priority || 0))
    : [];

  const emphasize = highlighted || isCurrentPlan;
  const TierIcon = TIER_ICONS[index] ?? TIER_ICONS[TIER_ICONS.length - 1];

  return (
    <Paper
      elevation={0}
      sx={{
        position: "relative",
        height: "100%",
        minHeight: 520,
        width: "100%",
        display: "flex",
        flexDirection: "column",
        borderRadius: 3,
        p: 3,
        border: "2px solid",
        borderColor: emphasize ? PRICING_ACCENT.main : "#ececec",
        bgcolor: emphasize ? "#fcf1f4" : "#fff",
        transition: "border-color 0.2s ease, box-shadow 0.2s ease",
        "&:hover": {
          borderColor: PRICING_ACCENT.main,
          boxShadow: "0 8px 24px rgba(180, 49, 74, 0.08)",
        },
      }}
    >
      {/* Badge / share */}
      {isCurrentPlan && (
        <Chip
          label="Current Plan"
          size="small"
          sx={{
            position: "absolute",
            top: 14,
            right: 14,
            height: 22,
            fontSize: "0.7rem",
            fontWeight: 700,
            bgcolor: PRICING_ACCENT.main,
            color: "#fff",
          }}
        />
      )}
      {!isCurrentPlan && isPaymentPending && (
        <Chip
          label="Payment in Progress"
          size="small"
          sx={{
            position: "absolute",
            top: 14,
            right: 14,
            height: 22,
            fontSize: "0.7rem",
            fontWeight: 700,
            bgcolor: PRICING_ACCENT.soft,
            color: PRICING_ACCENT.dark,
          }}
        />
      )}
      {!isCurrentPlan && !isPaymentPending && onShare && (
        <Tooltip title="Share plan" arrow>
          <Button
            onClick={(e) => {
              e.stopPropagation();
              onShare(plan);
            }}
            sx={{
              position: "absolute",
              top: 10,
              right: 10,
              minWidth: 0,
              width: 30,
              height: 30,
              p: 0,
              borderRadius: "50%",
              color: "text.secondary",
              "&:hover": { bgcolor: PRICING_ACCENT.soft, color: PRICING_ACCENT.main },
            }}
          >
            <ShareIcon sx={{ fontSize: "0.95rem" }} />
          </Button>
        </Tooltip>
      )}

      {/* Header: icon tile + name + tagline */}
      <Stack direction="row" spacing={1.25} alignItems="center" sx={{ pr: 4 }}>
        <Box
          sx={{
            width: 38,
            height: 38,
            borderRadius: 2,
            flexShrink: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            bgcolor: emphasize ? "#fff" : PRICING_ACCENT.soft,
            color: PRICING_ACCENT.main,
          }}
        >
          <TierIcon sx={{ fontSize: "1.15rem" }} />
        </Box>
        <Box sx={{ minWidth: 0 }}>
          <Typography noWrap sx={{ fontWeight: 700, fontSize: "1.2rem", lineHeight: 1.2, color: "text.primary" }}>
            {capitalizeWords(plan.plan_name)}
          </Typography>
          <Typography noWrap sx={{ fontSize: "0.8rem", color: "text.secondary", lineHeight: 1.3 }}>
            {tagline}
          </Typography>
        </Box>
      </Stack>

      {/* Hero price */}
      <Box sx={{ mt: 2.5 }}>
        {isFree ? (
          <Typography sx={{ fontWeight: 700, fontSize: "3rem", lineHeight: 1, color: "text.primary" }}>
            Free
          </Typography>
        ) : (
          <Box sx={{ display: "flex", alignItems: "baseline", flexWrap: "wrap" }}>
            <Typography sx={{ fontWeight: 700, fontSize: "3rem", lineHeight: 1, color: "text.primary" }}>
              {formatAmount(baseAmount, symbol)}
            </Typography>
            <Typography component="span" sx={{ fontSize: "1rem", color: "text.secondary", ml: 0.5 }}>
              /{cycleLabel}
              <Typography component="span" sx={{ fontSize: "0.72rem", color: "text.disabled", ml: 0.5 }}>
                + GST
              </Typography>
            </Typography>
          </Box>
        )}

        {/* Strikeout / helper line + yearly savings */}
        <Box
          sx={{
            mt: 1,
            minHeight: 22,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 1,
          }}
        >
          {isFree ? (
            <Typography sx={{ fontSize: "0.8rem", color: "text.secondary" }}>
              No credit card required
            </Typography>
          ) : strikeAmount > 0 && strikeAmount > baseAmount ? (
            <Typography
              sx={{ fontSize: "1.05rem", color: "text.disabled", textDecoration: "line-through", lineHeight: 1 }}
            >
              {formatAmount(strikeAmount, symbol)}
            </Typography>
          ) : (
            <span />
          )}

          {savingsAmount > 0 && (
            <Chip
              label={`Save ${formatAmount(savingsAmount, symbol)} yearly`}
              size="small"
              sx={{
                height: 22,
                fontSize: "0.7rem",
                fontWeight: 700,
                bgcolor: PRICING_ACCENT.soft,
                color: PRICING_ACCENT.dark,
                "& .MuiChip-label": { px: 1 },
              }}
            />
          )}
        </Box>
      </Box>

      {/* Pill CTA */}
      <Button
        fullWidth
        disableElevation
        variant={emphasize ? "contained" : "outlined"}
        disabled={isCurrentPlan || isPaymentPending}
        onClick={() => handleChoosePlan && handleChoosePlan(plan)}
        sx={{
          mt: 2.5,
          textTransform: "none",
          fontWeight: 600,
          fontSize: "0.95rem",
          py: 1.1,
          borderRadius: 999,
          ...(emphasize
            ? {
                bgcolor: PRICING_ACCENT.main,
                color: "#fff",
                "&:hover": { bgcolor: PRICING_ACCENT.dark },
                "&.Mui-disabled": { bgcolor: PRICING_ACCENT.main, color: "#fff", opacity: 0.5 },
              }
            : {
                color: PRICING_ACCENT.main,
                borderColor: "#e4c7ce",
                "&:hover": { borderColor: PRICING_ACCENT.main, bgcolor: PRICING_ACCENT.soft },
              }),
        }}
      >
        {cardButtonText}
      </Button>

      {/* Benefits heading + diamond-capped divider */}
      <Typography sx={{ mt: 3, mb: 1, fontWeight: 700, fontSize: "0.98rem", color: "text.primary" }}>
        Plan Benefits
      </Typography>
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
        <Box sx={{ width: 7, height: 7, flexShrink: 0, transform: "rotate(45deg)", bgcolor: PRICING_ACCENT.main }} />
        <Box sx={{ flex: 1, height: "1px", bgcolor: emphasize ? "#edd9de" : "#eee" }} />
        <Box sx={{ width: 7, height: 7, flexShrink: 0, transform: "rotate(45deg)", bgcolor: PRICING_ACCENT.main }} />
      </Box>

      {/* Feature list */}
      <Stack spacing={0.75} sx={{ mt: 2, flex: 1 }}>
        {features.map((feature, idx) => {
          const label = (feature.feature_name ?? "").split("_").join(" ");
          const FeatureIcon = getFeatureIcon(feature.feature_name ?? "");
          const [bold, rest] = splitEmphasis(label);
          return (
            <Box
              key={feature.subscription_plan_feature_uuid ?? idx}
              sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1 }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.9, minWidth: 0 }}>
                <FeatureIcon sx={{ fontSize: "0.95rem", color: PRICING_ACCENT.main, flexShrink: 0 }} />
                <Typography sx={{ fontSize: "0.85rem", color: "text.primary" }}>
                  {bold && (
                    <Typography component="span" sx={{ fontWeight: 700, fontSize: "0.85rem" }}>
                      {bold}{" "}
                    </Typography>
                  )}
                  {rest || (!bold ? label : "")}
                </Typography>
              </Box>
              {feature.description && (
                <Tooltip title={capitalizeWords(feature.description)} arrow placement="top">
                  <InfoOutlinedIcon
                    sx={{ fontSize: "0.9rem", color: "text.disabled", cursor: "pointer", flexShrink: 0 }}
                  />
                </Tooltip>
              )}
            </Box>
          );
        })}
      </Stack>

      {/* Billing mode (current plan only) */}
      {isCurrentPlan && currentBillingMode && (
        <Stack direction="row" spacing={1} alignItems="center" justifyContent="center" flexWrap="wrap" sx={{ mt: 2 }}>
          <Typography variant="body2" color="text.secondary">
            Billing mode:{" "}
            <Typography component="span" variant="body2" fontWeight={700} color="text.primary">
              {currentBillingMode === "RECURRING" ? "Recurring" : "Prepaid"}
            </Typography>
          </Typography>
          {onChangeBillingMode && (
            <Button
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                onChangeBillingMode(plan);
              }}
              sx={{ textTransform: "none", fontWeight: 700, minWidth: "auto", px: 1, color: PRICING_ACCENT.main }}
            >
              Change
            </Button>
          )}
        </Stack>
      )}
    </Paper>
  );
};

export default PricingPlanCard;

/**
 * Loading placeholder that mirrors the new card order (name/tagline,
 * hero price, pill CTA, benefits heading + divider, feature rows).
 */
export const PricingPlanCardSkeleton: React.FC<{ highlighted?: boolean }> = ({
  highlighted = false,
}) => {
  const featureWidths = ["70%", "58%", "76%", "62%", "54%"];
  return (
    <Paper
      elevation={0}
      sx={{
        width: "100%",
        minHeight: 520,
        display: "flex",
        flexDirection: "column",
        borderRadius: 3,
        p: 3,
        border: "2px solid",
        borderColor: highlighted ? PRICING_ACCENT.main : "#ececec",
        bgcolor: highlighted ? "#fcf1f4" : "#fff",
      }}
    >
      {/* header: icon tile + name/tagline */}
      <Stack direction="row" spacing={1.25} alignItems="center">
        <Skeleton variant="rounded" width={38} height={38} sx={{ borderRadius: 2, flexShrink: 0 }} />
        <Box sx={{ flex: 1 }}>
          <Skeleton variant="text" sx={{ fontSize: "1.2rem", width: "55%" }} />
          <Skeleton variant="text" sx={{ fontSize: "0.8rem", width: "72%" }} />
        </Box>
      </Stack>

      {/* hero price */}
      <Box sx={{ mt: 2.5 }}>
        <Skeleton variant="rounded" width="55%" height={46} sx={{ borderRadius: 1.5 }} />
        <Skeleton variant="text" sx={{ fontSize: "0.9rem", width: "28%", mt: 1 }} />
      </Box>

      {/* pill CTA */}
      <Skeleton variant="rounded" height={44} sx={{ borderRadius: 999, mt: 2.5 }} />

      {/* benefits heading */}
      <Skeleton variant="text" sx={{ fontSize: "0.98rem", width: "40%", mt: 3, mb: 1 }} />
      <Skeleton variant="rounded" height={2} sx={{ borderRadius: 1 }} />

      {/* feature rows */}
      <Stack spacing={0.75} sx={{ mt: 2, flex: 1 }}>
        {featureWidths.map((w, idx) => (
          <Stack key={idx} direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
            <Stack direction="row" spacing={0.9} alignItems="center" sx={{ flex: 1 }}>
              <Skeleton variant="circular" width={15} height={15} sx={{ flexShrink: 0 }} />
              <Skeleton variant="text" sx={{ fontSize: "0.85rem", width: w }} />
            </Stack>
            <Skeleton variant="circular" width={14} height={14} sx={{ flexShrink: 0 }} />
          </Stack>
        ))}
      </Stack>
    </Paper>
  );
};
